import React, { useContext, useEffect, useState } from "react";
import { TURBINE_DATA_TIMEOUT_MS } from "../../constants";
import { Card, Popup, Grid } from "semantic-ui-react";
import { ScreenSizes, Text } from "components/shared";
import styled, { ThemeContext } from "styled-components";
import PropTypes from "prop-types";

import "flag-icon-css/css/flag-icon.css";
import "responsive-semantic-ui.css";

import TurbineInfoModal from "containers/dashboard/TurbineInfoModal";
import CciDsoStatus, { isBoardDataFresh } from "components/live/CciDsoStatus";

const Wrapper = styled((props) => <Card.Header {...props} />)`
  background-color: ${(props) => props.themecontext.cardBackgroundColor}
    .HeaderSide {
    padding: 0.5rem;
  }
`;

const Name = styled((props) => <Text {...props} />)`
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  color: ${(props) => props.theme.textDark};
  font-weight: 900;
  text-overflow: ellipsis;
  align-items: center;
`;

const Power = styled((props) => <Text {...props} />)`
  color: ${(props) => props.theme.textDark};
  font-weight: 100;
  white-space: nowrap;
`;

// Name/state block (turbine name + "Current state" text) - the fullDetails
// padding values below (1rem outer + 1rem/1rem per row) were sized for
// desktop, where there's plenty of room to spare. On mobile the same
// padding, stacked with the tab bar right below it (see TabsColumn), left
// a visibly dead gap between "Stopped"/the turbine name and the tabs -
// wasted space on an already-small screen. Cut down specifically on
// mobile, everywhere else unaffected.
const StatesGrid = styled((props) => <Grid {...props} />)`
  padding: 1rem;
  ${ScreenSizes.lessThan("mobile")`
    padding: 0.5rem;
  `}
`;

const NameRow = styled((props) => <Grid.Row {...props} />)`
  padding: ${(props) => (props.fulldetails ? "1rem 0rem 0rem 0rem" : "0rem")};
  ${ScreenSizes.lessThan("mobile")`
    padding: 0.4rem 0rem 0rem 0rem !important;
  `}
`;

const StateRow = styled((props) => <Grid.Row {...props} />)`
  padding: ${(props) => (props.fulldetails ? "1rem 0rem 1rem 0rem" : "0rem")};
  justify-content: space-between;
  ${ScreenSizes.lessThan("mobile")`
    padding: 0.15rem 0rem 0.4rem 0rem !important;
  `}
`;

const MainGridColumn = styled((props) => <Grid.Column {...props} />)`
  flex: ${(props) => (props.fullDetails ? 1 : undefined)};
  margin: auto;
`;

// The LIVE/HISTORY/ADVANCED/SERVICE LOGS tab menu (renderMenu()) - on
// mobile, this Grid is `stackable`, so it drops from a side-by-side column
// next to the turbine name onto its own row below it. The menu itself
// stays a single horizontal row via NavigationLinks' `mobileCompact` prop
// (see Details.js's renderMenu() and components/shared/menu/index.js) -
// that's what actually controls Semantic's flex-direction (a first attempt
// here fought it with flex-wrap overrides, which don't touch
// flex-direction and did nothing). This column just needs to not force a
// narrower width than the row of items wants, and get out of the way.
const TabsColumn = styled(MainGridColumn)`
  ${ScreenSizes.lessThan("mobile")`
    width: 100% !important;
  `}
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.5rem;
  flex-shrink: 0;
  background-color: ${(props) => props.color};
`;

// margin, not StateRow's justify-content: Semantic's ".ui.grid > .row"
// (justify-content: inherit) outranks the styled-component's single class.
const CciDsoSlot = styled.div`
  margin-left: auto;
`;

const DATA_DELAY = TURBINE_DATA_TIMEOUT_MS;

const getSignal = (turbineState) => {
  // An explicit online:false from the bridge's MQTT status message (clean
  // MQTT disconnect via LWT, or a PLC/Modbus failure - see mqttIngest.js)
  // means "actually offline right now" - don't wait out the rest of the
  // DATA_DELAY grace window in that case.
  if (turbineState && (turbineState.online === false || turbineState.plcOnline === false)) return 0;
  return turbineState && turbineState.lastUpdate
    ? (turbineState.lastUpdate - Date.now() + DATA_DELAY) / 1000
    : 0;
};

const CardHeader = ({ renderMenu, fullDetails, windTurbine, liveProps, turbineState, cciDsoProps }) => {
  const { state, err } = liveProps;
  // windTurbine can be momentarily undefined during navigation between two
  // turbines' detail pages (confirmed live: "Cannot read properties of
  // undefined (reading 'name')", crashing the whole page with no error
  // boundary to catch it) - Details.js's own loading/empty-state branching
  // only guards the *first* render of this Content block, not a
  // subsequent one if windTurbineData flips back to falsy while this
  // component is still mounted (e.g. windTurbine local state resetting
  // faster than the new turbine's data arrives). Defensive here rather
  // than chasing the exact race in Details.js's state.
  const { name } = windTurbine || {};
  const themeContext = useContext(ThemeContext);
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  // getSignal() below only re-evaluates when this component re-renders, which
  // otherwise only happens when new turbineState/liveProps props arrive - if
  // the turbine goes silent (Modbus/Ethernet/MQTT loss), nothing would ever
  // trigger a re-render again and the status dot would stay frozen on
  // whatever it last showed. This tick forces a re-render every couple
  // seconds purely so the elapsed-time check below gets re-run.
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 2000);
    return () => clearInterval(id);
  }, []);

  // Bridge online but its PLC not answering Modbus: warning colour, not the
  // grey "offline" - the board is at the turbine and reachable.
  const plcDown =
    !!turbineState &&
    turbineState.online === true &&
    turbineState.plcOnline === false;

  const statusColor = !!err && err !== 0
    ? themeContext.critColor
    : getSignal(turbineState) > 0
      ? themeContext.okColor
      : plcDown
        ? themeContext.warnColor
        : themeContext.offlineColor;

  const renderLiveStates = () => (
    <StatesGrid>
      <NameRow fulldetails={fullDetails}>
        <Popup
          content="Click for info"
          trigger={
            <Name
              style={{
                marginRight: fullDetails ? undefined : "auto",
                padding: "0rem",
                minHeight: "2rem",
                cursor: "pointer",
              }}
              size={fullDetails ? 24 : 16}
              onClick={() => setInfoModalOpen(true)}
            >
              <StatusDot color={statusColor} />
              {name}
            </Name>
          }
        />
      </NameRow>
      <StateRow fulldetails={fullDetails}>
        <Popup
          content="Current state"
          trigger={
            <Text style={{ color: "gray" }} size={fullDetails ? 24 : 16}>
              {state && state.length > 0 ? state : "N/A"}
            </Text>
          }
        />
        {/* Dashboard card: same CCI/DSO state as the Live tab, right of the
            state text, so a dropped CCI link / DSO curtailment shows at a
            glance on every PF2 turbine ("—" for the others). The Live tab
            has its own, bigger indicators above the graph. */}
        {!fullDetails && (
          <CciDsoSlot>
            <CciDsoStatus
              compact
              justify="flex-end"
              liveProps={cciDsoProps}
              connected={isBoardDataFresh(turbineState)}
            />
          </CciDsoSlot>
        )}
      </StateRow>
    </StatesGrid>
  );

  return (
    <Wrapper themecontext={themeContext}>
      <Grid
        stretched
        stackable
        verticalAlign="middle"
        padded
        columns={fullDetails ? 2 : 1}
      >
        <Grid.Row>
          <MainGridColumn fulldetails={fullDetails ? 1 : 0}>
            {renderLiveStates()}
          </MainGridColumn>
          <TabsColumn className={!fullDetails ? "computer only" : ""}>
            {renderMenu()}
          </TabsColumn>
        </Grid.Row>
      </Grid>
      {windTurbine && (
        <TurbineInfoModal
          open={infoModalOpen}
          onClose={() => setInfoModalOpen(false)}
          windTurbine={windTurbine}
        />
      )}
    </Wrapper>
  );
};

CardHeader.propTypes = {
  renderMenu: PropTypes.func,
  hideRight: PropTypes.bool,
  hideRPM: PropTypes.bool,
  fullDetails: PropTypes.bool,
  turbineState: PropTypes.object,
  cciDsoProps: PropTypes.object,
};

CardHeader.defaultProps = {
  renderMenu: () => null,
  hideRight: false,
  hideRPM: false,
  fullDetails: false,
  turbineState: undefined,
  cciDsoProps: {},
};

export default React.memo(CardHeader);
